import React from 'react';
import { Check, AlertCircle } from 'lucide-react';
import type { PasswordCheck, PasswordTone } from '../../lib/passwordPolicy';

interface Props {
  check: PasswordCheck;
}

const SEGMENT_COLOR: Record<PasswordTone, string> = {
  none: 'bg-ink-100',
  weak: 'bg-ruby-500',
  medium: 'bg-gold-500',
  strong: 'bg-green-700',
};

const LABEL_COLOR: Record<PasswordTone, string> = {
  none: 'text-ink-500',
  weak: 'text-ruby-700',
  medium: 'text-gold-600',
  strong: 'text-green-700',
};

export function PasswordStrengthMeter({ check }: Props) {
  const { score, tone, label, hints, rules } = check;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 grid grid-cols-3 gap-1.5">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={[
                'h-1.5 rounded-pill transition-colors duration-base',
                score >= i ? SEGMENT_COLOR[tone] : 'bg-ink-100',
              ].join(' ')}
            />
          ))}
        </div>
        {label && (
          <span className={`text-caption font-semibold font-body ${LABEL_COLOR[tone]}`}>
            {label}
          </span>
        )}
      </div>

      <ul className="space-y-1">
        {rules.map((r) => (
          <li
            key={r.label}
            className={[
              'flex items-start gap-1.5 text-caption font-body',
              r.passed ? 'text-green-700' : 'text-ink-500',
            ].join(' ')}
          >
            {r.passed ? (
              <Check size={13} className="shrink-0 mt-0.5" strokeWidth={2.5} />
            ) : (
              <span className="shrink-0 mt-1 w-[11px] h-[11px] rounded-full border border-ink-300" />
            )}
            <span>{r.label}</span>
          </li>
        ))}
        {hints.map((h) => (
          <li
            key={h}
            className="flex items-start gap-1.5 text-caption font-body text-ruby-700"
          >
            <AlertCircle size={13} className="shrink-0 mt-0.5" />
            <span>{h}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
